import styled from 'styled-components';
import { Btn } from './sharedButtons.styles';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1.5rem;
  animation: fadeIn 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes fadeIn {
    from { opacity: 0 }
    to { opacity: 1 }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-end;
  }
`;

const Modal = styled.div`
  background: var(--background-primary);
  border-radius: 14px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--border-color);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  animation: slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0 }
    to { transform: translateY(0); opacity: 1 }
  }

  @media (max-width: 768px) {
    border-radius: 16px 16px 0 0;
    max-width: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const ModalHead = styled.header`
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const ModalIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${(p) => p.$tint || 'rgba(184, 149, 106, 0.12)'};
  color: ${(p) => p.$color || 'var(--primary-color)'};
  flex-shrink: 0;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFoot = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--background-secondary);
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 13px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  font-size: 14px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  box-sizing: border-box;
  transition: all 0.2s ease;
  color: var(--text-primary);
  background: var(--background-primary);

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const PresetChip = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${(p) => (p.$active ? 'rgba(184, 149, 106, 0.15)' : 'var(--background-primary)')};
  color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    background: ${(p) => (p.$active ? 'rgba(184, 149, 106, 0.25)' : 'var(--background-secondary)')};
    border-color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-light)')};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 10px 13px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  font-size: 14px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  resize: vertical;
  box-sizing: border-box;
  color: var(--text-primary);
  background: var(--background-primary);
  transition: all 0.2s ease;
  line-height: 1.5;

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }
`;

const HistoryBox = styled.section`
  margin-top: 16px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: rgba(248, 247, 244, 0.55);

  body.dark-mode & {
    background: var(--background-tertiary);
    border-color: var(--border-color);
  }
`;

const HistoryTitle = styled.div`
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow: auto;
`;

const HistoryItem = styled.div`
  padding: 9px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);

  body.dark-mode & {
    background: var(--background-quaternary);
    border-color: var(--border-color);
  }
`;

const HistoryItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
`;

const HistoryMeta = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  color: var(--text-muted);
  line-height: 1.45;
`;

const HistoryEmpty = styled.div`
  font-size: 12.5px;
  color: var(--text-muted);
  line-height: 1.45;
`;

const HintText = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const HighlightText = styled.span`
  color: var(--text-primary, #1a202c);
  font-weight: 650;
`;

const CancelBtn = styled(Btn)`
  background: transparent;
  color: var(--text-secondary);
  border-color: var(--border-color);

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-light);
    color: var(--text-primary);
  }
`;

const ConfirmDenyBtn = styled(Btn)`
  background: var(--error-color);
  color: #fff;
  box-shadow: 0 2px 6px rgba(229, 62, 62, 0.15);

  &:hover:not(:disabled) {
    background: var(--error-dark);
    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.25);
    transform: translateY(-1px);
  }
`;


// Styled-components for AdminExtensions, extracted VERBATIM. In-module
// ${Name} component-selector interpolations still resolve (all names here).


export {
  Overlay, Modal, ModalHead, ModalIconBadge, ModalBody, ModalFoot,
  Label, Input, PresetRow, PresetChip, Textarea, HistoryBox,
  HistoryTitle, HistoryList, HistoryItem, HistoryItemTop, HistoryMeta, HistoryEmpty,
  HintText, HighlightText, CancelBtn, ConfirmDenyBtn,
};
